import LogoImage from '@assets/svg/standard-logo.svg';
import { useAuth0 } from '@auth0/auth0-react';
import { Loading } from '@components';
import { StudioExpiredAlert } from '@components/StudioExpiredAlert/StudioExpiredAlert';
import { yupResolver } from '@hookform/resolvers/yup';
import { useWindowEvent } from '@mantine/hooks';
// import { trackOrganizationSubmitted } from '@services/mixpanel/trackers/UserSessionMetrics';
import { useAppDispatch, useAppSelector } from '@store/redux/store';
import { noop } from '@utils/noop';
import { useEffect, useState } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useCypressHelper } from './hooks/useCypressHelper';
import { useOrgIdQuery } from './hooks/useOrgIdQuery';
import { useOrgQuery } from './hooks/useOrgQuery';

import {
  Body,
  Button,
  Container,
  Description,
  ErrorContainer,
  HelperText,
  Input,
} from './Organization.styles';
import { OrgFormSchema, OrgStates } from './Organization.types';
import { getInitialState } from './utils/getInitialState';
import {
  initialFormState,
  submitWithHook,
  SubmitWithHookArgs,
  schema,
} from './utils/onOrgNameSubmit';
import { signIn } from './utils/signIn';

/**
 *  {@link https://stately.ai/registry/editor/share/eb93bc0c-0b32-4f11-b5b6-576d324ac410 state machine}
 */
const Organization = () => {
  // Prevent serving from bf cache.
  useWindowEvent('unload', noop);

  const dispatch = useAppDispatch();
  const { organization } = useAppSelector();
  const { loginWithRedirect } = useAuth0();
  const { handleSubmit, control, reset } = useForm<OrgFormSchema>({
    defaultValues: initialFormState,
    resolver: yupResolver(schema),
  });
  const emailController = useController({
    control,
    name: 'email',
  });
  const [orgFormState, setOrgFormState] = useState<OrgStates>(() =>
    getInitialState(organization.orgId),
  );

  const [isFocused, setIsFocused] = useState<boolean>(false);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const orgIdQuery = useOrgIdQuery({
    email: emailController.field.value,
    isSubmitSuccessful: orgFormState === 'submitted',
    setOrgFormState,
  });

  useEffect(() => {
    if (orgIdQuery.data && orgFormState === 'submitted') {
      const studioExpirationDate = orgIdQuery.data.studio?.paidExpirationDate;
      const studioExpirationDateTime = studioExpirationDate
        ? new Date(studioExpirationDate).getTime()
        : undefined;
      // Reset back to a state we can handle. Redirecting, which is similar to error as you can submit from it.
      setOrgFormState('redirecting');
      signIn(
        orgIdQuery.data.orgId,
        dispatch,
        loginWithRedirect,
        emailController.field.value,
        studioExpirationDateTime,
      );
    }
  }, [orgIdQuery.data, orgFormState]);

  const { isLoading } = useOrgQuery(
    organization,
    orgFormState,
    setOrgFormState,
  );
  useCypressHelper();

  // const expirationDate = data?.studio?.paidExpirationDate
  //   ? data.studio.paidExpirationDate.getTime()
  //   : undefined;
  const { refetch, failureCount } = orgIdQuery;
  const args: SubmitWithHookArgs = [
    setOrgFormState,
    failureCount,
    refetch,
    reset,
  ];
  // Studio expired is handled before this is used
  const isNFError = orgFormState === 'error' || orgFormState === 'malicious';

  if (isLoading) {
    return <Loading />;
  }

  if (orgFormState === 'expired') {
    return <StudioExpiredAlert />;
  }

  const handleKeyDown = (event: any) => {
    if (event.key === 'Enter') {
      handleSubmit(submitWithHook(...args))();
    }
  };

  return (
    <Container
      onSubmit={handleSubmit(submitWithHook(...args))}
      data-testid="login-form"
    >
      <Body>
        <img
          src={LogoImage}
          className="Organization_logo"
          alt="logo"
          height="28px"
        />

        <div style={{ height: 24 }} />
        <div className="Organization_welcomeGroup">
          <Description className="welcome">
            Welcome to the Beam platform!
          </Description>
          <Description className="welcome-extra">
            Please enter your Email.
          </Description>
        </div>

        <div style={{ height: 24 }} />

        <HelperText isFocused={isFocused} htmlFor="orgInput">
          Email address
        </HelperText>
        <Input
          id="orgInput"
          className="orgInput"
          placeholder=""
          {...emailController.field}
          autoFocus
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />

        {isNFError && (
          <ErrorContainer data-testid="login-error">
            Email not found
          </ErrorContainer>
        )}

        <div style={{ height: 24 }} />

        <Button
          type="submit"
          disabled={orgIdQuery.isLoading || orgFormState === 'redirecting'}
        >
          Next
        </Button>

        {/* <div style={{ height: 16 }} /> */}

        {/* {!isQueryError && data && (
          <Continue
            onClick={() => {
              trackOrganizationSubmitted(data.displayName);
              signIn(
                // On a revisit, orgId and exp date have been saved
                organization.orgId,
                dispatch,
                loginWithRedirect,
                emailController.field.value,
                expirationDate,
              );
            }}
            data-testid="prev-org"
          >{`Continue with ${data.displayName}`}</Continue>
        )} */}
      </Body>
    </Container>
  );
};

export { Organization };
